'use client';

import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import DspDeliveryTable from './components/DspDeliveryTable';
import AudienceDemographics from './components/AudienceDemographics';

const PerformanceChart = dynamic(() => import('./components/PerformanceChart'), { ssr: false });

interface AnalyticsSummary {
  totalStreams: number;
  totalEarnings: number;
  liveReleases: number;
  activeDspCount: number;
  totalReleases: number;
}

interface ReleaseRow {
  id: string;
  title: string;
  releaseType: string;
  status: string;
  streams30d: number;
  totalStreams: number;
  totalEarnings: number;
  createdAt: string;
  dspAvailability: { dsp: string; available: boolean; streams: number }[];
  deliveryStatus: 'delivered' | 'processing' | 'failed' | 'draft';
}

interface DspData {
  dsp: string;
  streams: number;
  earnings: number;
  releases: number;
}

interface TerritoryData {
  territory: string;
  streams: number;
  earnings: number;
}

interface TrendPoint {
  month: string;
  streams: number;
  earnings: number;
}

interface AnalyticsData {
  summary: AnalyticsSummary;
  perRelease: ReleaseRow[];
  dspPerformance: DspData[];
  territoryBreakdown: TerritoryData[];
  performanceTrend: TrendPoint[];
}

type TabId = 'overview' | 'dsp-delivery' | 'demographics' | 'performance';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'ChartBarIcon' },
  { id: 'dsp-delivery', label: 'DSP Delivery', icon: 'GlobeAltIcon' },
  { id: 'demographics', label: 'Audience', icon: 'UsersIcon' },
  { id: 'performance', label: 'Performance', icon: 'ArrowTrendingUpIcon' },
];

function formatInr(val: number) {
  return `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AnalyticsPage() {
  const [tab, setTab] = useState<TabId>('overview');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/analytics/detailed');
        if (!res.ok) throw new Error('Failed to load analytics');
        const json = await res.json();
        setData(json);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  function handleExport() {
    if (!data) return;
    setExporting(true);

    try {
      // Sheet 1: Summary
      const summaryRows = [
        ['TubeNexa Analytics Export'],
        ['Generated:', new Date().toLocaleString('en-IN')],
        [],
        ['SUMMARY'],
        ['Total Streams', data.summary.totalStreams],
        ['Total Earnings (INR)', data.summary.totalEarnings],
        ['Live Releases', data.summary.liveReleases],
        ['Active DSPs', data.summary.activeDspCount],
        ['Total Releases', data.summary.totalReleases],
        [],
        ['PER-RELEASE DSP DELIVERY'],
        ['Release', 'Type', 'Status', '30d Streams', 'Total Streams', 'Total Earnings (INR)', 'Available DSPs'],
        ...data.perRelease.map((r) => [
          r.title,
          r.releaseType,
          r.deliveryStatus,
          r.streams30d,
          r.totalStreams,
          r.totalEarnings,
          r.dspAvailability.filter((d) => d.available).map((d) => d.dsp).join('; '),
        ]),
        [],
        ['DSP PERFORMANCE'],
        ['DSP', 'Streams', 'Earnings (INR)', 'Releases'],
        ...data.dspPerformance.map((d) => [d.dsp, d.streams, d.earnings, d.releases]),
        [],
        ['TERRITORY BREAKDOWN'],
        ['Territory', 'Streams', 'Earnings (INR)'],
        ...data.territoryBreakdown.map((t) => [t.territory, t.streams, t.earnings]),
        [],
        ['PERFORMANCE TREND'],
        ['Month', 'Streams', 'Earnings (INR)'],
        ...data.performanceTrend.map((p) => [p.month, p.streams, p.earnings]),
      ];

      const csv = summaryRows.map((row) => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tubenexa_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  const kpis = data
    ? [
        {
          id: 'kpi-streams',
          label: 'Total Streams',
          value: Number(data.summary.totalStreams).toLocaleString('en-IN'),
          icon: 'ChartBarIcon',
          color: 'var(--primary-light)',
          bg: 'var(--purple-bg)',
          sub: 'All-time across all DSPs',
        },
        {
          id: 'kpi-earnings',
          label: 'Total Earnings',
          value: formatInr(data.summary.totalEarnings),
          icon: 'BanknotesIcon',
          color: 'var(--warning)',
          bg: 'var(--warning-bg)',
          sub: 'Cumulative royalties',
        },
        {
          id: 'kpi-live',
          label: 'Live Releases',
          value: `${data.summary.liveReleases} / ${data.summary.totalReleases}`,
          icon: 'MusicalNoteIcon',
          color: 'var(--success)',
          bg: 'var(--success-bg)',
          sub: 'Delivered to stores',
        },
        {
          id: 'kpi-dsps',
          label: 'Active DSPs',
          value: data.summary.activeDspCount.toString(),
          icon: 'GlobeAltIcon',
          color: '#00A8E1',
          bg: 'rgba(0,168,225,0.12)',
          sub: 'Platforms generating streams',
        },
      ]
    : [];

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Analytics</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Per-release DSP availability, store delivery, audience demographics, and performance over time
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={!data || exporting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-opacity disabled:opacity-50"
            style={{ background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
          >
            <Icon name="ArrowDownTrayIcon" size={16} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        {/* KPI Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-2xl p-5 animate-pulse"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', height: '110px' }}
              />
            ))}
          </div>
        ) : error ? (
          <div
            className="rounded-2xl p-8 text-center"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <Icon name="ExclamationCircleIcon" size={36} className="mx-auto mb-3" style={{ color: 'var(--danger)' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--danger)' }}>{error}</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Check your connection and try refreshing.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((kpi) => (
              <div
                key={kpi.id}
                className="rounded-2xl p-5"
                style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: kpi.bg }}
                  >
                    <Icon name={kpi.icon as any} size={20} style={{ color: kpi.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold font-mono-data" style={{ color: 'var(--foreground)' }}>{kpi.value}</p>
                <p className="text-xs font-semibold mt-0.5" style={{ color: 'var(--foreground)' }}>{kpi.label}</p>
                <p className="text-2xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{kpi.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        {!loading && !error && data && (
          <>
            <div className="flex items-center gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  style={{
                    background: tab === t.id ? 'var(--primary)' : 'transparent',
                    color: tab === t.id ? 'white' : 'var(--muted-foreground)',
                  }}
                >
                  <Icon name={t.icon as any} size={14} />
                  <span className="hidden sm:inline">{t.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div>
              {/* Overview Tab */}
              {tab === 'overview' && (
                <div className="space-y-6">
                  {/* Performance chart preview */}
                  <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                      <div>
                        <h3 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Performance Over Time</h3>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Monthly streams and earnings trend</p>
                      </div>
                      <button
                        onClick={() => setTab('performance')}
                        className="text-xs font-semibold flex items-center gap-1"
                        style={{ color: 'var(--primary-light)' }}
                      >
                        View full chart <Icon name="ArrowRightIcon" size={12} />
                      </button>
                    </div>
                    <PerformanceChart data={data.performanceTrend} />
                  </div>

                  {/* DSP summary grid */}
                  <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <h3 className="text-base font-semibold mb-5" style={{ color: 'var(--foreground)' }}>DSP Overview</h3>
                    {data.dspPerformance.length === 0 ? (
                      <p className="text-sm text-center py-8" style={{ color: 'var(--muted-foreground)' }}>No DSP data yet. Releases go live once QC passes.</p>
                    ) : (
                      <div className="space-y-3">
                        {data.dspPerformance
                          .sort((a, b) => b.streams - a.streams)
                          .map((d) => {
                            const maxStreams = Math.max(...data.dspPerformance.map((x) => x.streams), 1);
                            const pct = Math.round((d.streams / maxStreams) * 100);
                            const DSP_COLORS: Record<string, string> = {
                              'Spotify': '#1DB954', 'Apple Music': '#FC3C44', 'YouTube Music': '#FF0000',
                              'Amazon Music': '#00A8E1', 'Tidal': '#00FFFF', 'JioSaavn': '#2BC5B4',
                              'Gaana': '#E72C30', 'Wynk': '#7B2FBE',
                            };
                            const color = DSP_COLORS[d.dsp] || 'var(--primary)';
                            return (
                              <div key={d.dsp} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                                    <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{d.dsp}</span>
                                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{d.releases} release{d.releases !== 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-sm font-bold font-mono-data" style={{ color: 'var(--foreground)' }}>
                                      {Number(d.streams).toLocaleString('en-IN')}
                                    </span>
                                    <span className="text-xs ml-2" style={{ color: 'var(--warning)' }}>
                                      {formatInr(d.earnings)}
                                    </span>
                                  </div>
                                </div>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                                  <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${pct}%`, background: color }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* DSP Delivery Tab */}
              {tab === 'dsp-delivery' && (
                <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <div>
                      <h3 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Per-Release DSP Availability</h3>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                        Click a release to see store-by-store delivery status
                      </p>
                    </div>
                    <div
                      className="flex items-center gap-4 text-xs"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--success)' }} />
                        Live
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--warning)' }} />
                        Processing
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--danger)' }} />
                        Failed
                      </div>
                    </div>
                  </div>
                  <DspDeliveryTable releases={data.perRelease} />
                </div>
              )}

              {/* Demographics Tab */}
              {tab === 'demographics' && (
                <AudienceDemographics
                  territories={data.territoryBreakdown}
                  dspPerformance={data.dspPerformance}
                />
              )}

              {/* Performance Tab */}
              {tab === 'performance' && (
                <div className="rounded-2xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                    <div>
                      <h3 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Performance Over Time</h3>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                        Monthly streams and earnings — last 12 months
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full" style={{ background: 'var(--primary)' }} />
                        Streams
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--warning)' }} />
                        Earnings
                      </div>
                    </div>
                  </div>
                  <PerformanceChart data={data.performanceTrend} />

                  {/* Monthly table */}
                  {data.performanceTrend.length > 0 && (
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['Month', 'Streams', 'Earnings (INR)'].map((h) => (
                              <th
                                key={h}
                                className="text-left pb-3 pr-6 text-xs font-semibold uppercase tracking-wide"
                                style={{ color: 'var(--muted-foreground)' }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...data.performanceTrend].reverse().map((p) => (
                            <tr key={p.month} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td className="py-3 pr-6 text-xs font-medium" style={{ color: 'var(--foreground)' }}>{p.month}</td>
                              <td className="py-3 pr-6 font-mono-data text-xs" style={{ color: 'var(--primary-light)' }}>
                                {Number(p.streams).toLocaleString('en-IN')}
                              </td>
                              <td className="py-3 font-mono-data text-xs font-semibold" style={{ color: 'var(--warning)' }}>
                                {formatInr(p.earnings)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
